import React, { useId, useMemo } from 'react';
import {
  ArrowUpRight,
  AlertTriangle,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Minus,
  TrendingDown,
} from 'lucide-react';

/**
 * DecisionWidget
 *
 * v2 decision-system widget.
 * Renders backend truth instead of inventing interpretation in the UI.
 *
 * Expected API fields:
 * - route
 * - decision_summary
 * - fare_value_band
 * - timing_risk_band
 * - signal_quality
 * - freshness_state
 * - override_active
 * - override_reason
 * - decision_id (optional)
 *
 * Legacy fields are tolerated where possible.
 */
export default function DecisionWidget({
  prediction,
  debug = false,
  variant = 'full',
  className = '',
  showFooter = true,
}) {
  const styleScopeId = useId().replace(/:/g, '');
  const stylePrefix = `mizar-dw-${styleScopeId}`;

  const normalized = useMemo(() => normalizePrediction(prediction), [prediction]);

  if (!normalized.hasRenderableSignal) return null;

  const {
    routeLabel,
    decisionSummary,
    fareValueBand,
    timingRiskBand,
    signalQuality,
    freshnessState,
    overrideActive,
    overrideReason,
    decisionId,
    themeKey,
  } = normalized;

  const rootClassName = [
    `${stylePrefix}-widget`,
    `${stylePrefix}-${themeKey}`,
    `${stylePrefix}-${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <style>{buildScopedStyles(stylePrefix)}</style>

      <section className={rootClassName} aria-label="MIZAR decision widget">
        <div className={`${stylePrefix}-inner`}>
          <div className={`${stylePrefix}-badgeRow`}>
            {routeLabel ? (
              <span className={`${stylePrefix}-routeBadge`}>
                <ArrowUpRight size={13} />
                {routeLabel}
              </span>
            ) : null}

            <span className={`${stylePrefix}-signalBadge`}>
              <Sparkles size={13} />
              Live booking signal
            </span>
          </div>

          <h3 className={`${stylePrefix}-headline`}>
            {overrideActive ? 'Signal needs caution' : 'Booking decision summary'}
          </h3>

          <div className={`${stylePrefix}-summary`}>
            {decisionSummary}
          </div>

          <div className={`${stylePrefix}-grid`}>
            <div className={`${stylePrefix}-panel ${stylePrefix}-panelStrong`}>
              <div className={`${stylePrefix}-panelTitle`}>
                <ShieldCheck size={16} />
                Fare value
              </div>

              <div className={`${stylePrefix}-panelValue`}>
                {formatFareValueBand(fareValueBand)}
              </div>

              <div className={`${stylePrefix}-panelHelp`}>
                {formatFareValueHelp(fareValueBand)}
              </div>
            </div>

            <div className={`${stylePrefix}-panel`}>
              <div className={`${stylePrefix}-panelTitle`}>
                {getTimingIcon(timingRiskBand)}
                Timing signal
              </div>

              <div className={`${stylePrefix}-panelValue`}>
                {formatTimingRiskBand(timingRiskBand)}
              </div>

              <div className={`${stylePrefix}-panelHelp`}>
                {formatTimingRiskHelp(timingRiskBand, overrideActive)}
              </div>
            </div>
          </div>

          <div className={`${stylePrefix}-metaRow`}>
            <span className={`${stylePrefix}-metaChip`}>
              Signal quality: <strong>{formatSignalQuality(signalQuality)}</strong>
            </span>
            <span className={`${stylePrefix}-metaChip`}>
              Freshness: <strong>{formatFreshnessState(freshnessState)}</strong>
            </span>
          </div>

          {overrideActive ? (
            <div className={`${stylePrefix}-overrideBox`}>
              <AlertTriangle size={16} />
              <span>{formatOverrideReason(overrideReason)}</span>
            </div>
          ) : null}

          {showFooter && variant === 'full' ? (
            <div className={`${stylePrefix}-footer`}>
              <div className={`${stylePrefix}-footerCopy`}>
                <strong>MIZAR insight:</strong> this signal combines price position and timing risk to support the booking decision moment.
              </div>
            </div>
          ) : null}

          {debug && decisionId ? (
            <div className={`${stylePrefix}-debug`}>
              Decision ID: {decisionId}
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}

function normalizePrediction(prediction) {
  const safe = prediction && typeof prediction === 'object' ? prediction : {};

  const routeLabel = getRouteLabel(safe);

  const fareValueBand =
    toNullableString(
      safe.fare_value_band ??
      safe.fareValueBand ??
      inferFareBandFromLegacyFields(safe)
    );

  const timingRiskBand =
    toNullableString(
      safe.timing_risk_band ??
      safe.timingRiskBand ??
      inferTimingBandFromLegacyFields(safe)
    );

  const signalQuality =
    toNullableString(
      safe.signal_quality ??
      safe.signalQuality
    ) ?? 'partial';

  const freshnessState =
    toNullableString(
      safe.freshness_state ??
      safe.freshnessState
    ) ?? 'recent';

  const overrideActive = Boolean(
    safe.override_active ??
    safe.overrideActive ??
    false
  );

  const overrideReason =
    toNullableString(
      safe.override_reason ??
      safe.overrideReason
    );

  const decisionSummary =
    toNullableString(
      safe.decision_summary ??
      safe.decisionSummary
    ) ??
    buildFallbackSummary({
      fareValueBand,
      timingRiskBand,
      overrideActive,
      overrideReason,
    });

  const decisionId =
    safe.decision_id ??
    safe.decisionId ??
    safe.id ??
    null;

  const hasRenderableSignal =
    Boolean(routeLabel) ||
    Boolean(decisionSummary) ||
    Boolean(fareValueBand) ||
    Boolean(timingRiskBand) ||
    overrideActive;

  return {
    hasRenderableSignal,
    routeLabel,
    decisionSummary,
    fareValueBand,
    timingRiskBand,
    signalQuality,
    freshnessState,
    overrideActive,
    overrideReason,
    decisionId,
    themeKey: getThemeKey({ timingRiskBand, overrideActive }),
  };
}

function inferFareBandFromLegacyFields(prediction) {
  const z =
    toNumber(prediction.fare_value_zscore) ??
    toNumber(prediction.price_z_score);

  if (z === null) return null;
  if (z <= -1.5) return 'cheap';
  if (z <= -0.5) return 'good';
  if (z < 0.5) return 'fair';
  if (z < 1.5) return 'expensive';
  return 'very_expensive';
}

function inferTimingBandFromLegacyFields(prediction) {
  const rawScore = toNumber(
    prediction.regret_risk_score ??
    prediction.regretRiskScore ??
    prediction.score ??
    prediction.risk_score ??
    prediction.regret_risk
  );

  if (rawScore === null) return null;
  if (rawScore < 0.40) return 'low';
  if (rawScore < 0.70) return 'medium';
  return 'high';
}

function buildFallbackSummary({
  fareValueBand,
  timingRiskBand,
  overrideActive,
  overrideReason,
}) {
  if (overrideActive) {
    return formatOverrideReason(overrideReason);
  }

  if (
    (fareValueBand === 'cheap' || fareValueBand === 'good') &&
    timingRiskBand === 'low'
  ) {
    return 'This fare sits below the normal range for this route, and there is no strong pressure to act immediately.';
  }

  if (
    (fareValueBand === 'cheap' || fareValueBand === 'good') &&
    timingRiskBand === 'high'
  ) {
    return 'This fare sits below the normal range for this route, and waiting increases the risk of losing it.';
  }

  if (
    (fareValueBand === 'expensive' || fareValueBand === 'very_expensive') &&
    timingRiskBand === 'low'
  ) {
    return 'This fare is above the normal range for this route, but there is no strong immediate pressure to book.';
  }

  if (
    (fareValueBand === 'expensive' || fareValueBand === 'very_expensive') &&
    timingRiskBand === 'high'
  ) {
    return 'This fare is already elevated for this route, and current signals suggest waiting could make it worse.';
  }

  if (fareValueBand === 'fair' && timingRiskBand === 'low') {
    return 'This fare looks broadly in line with the normal range for this route, with no strong pressure to act immediately.';
  }

  if (fareValueBand === 'fair' && timingRiskBand === 'medium') {
    return 'This fare looks broadly in line with the normal range for this route, but there is some movement worth watching.';
  }

  if (fareValueBand === 'fair' && timingRiskBand === 'high') {
    return 'This fare looks broadly in line with the normal range for this route, but waiting carries increasing risk.';
  }

  return 'The current signal is directional, but not strong enough to support a more decisive booking judgement.';
}

function getThemeKey({ timingRiskBand, overrideActive }) {
  if (overrideActive) return 'caution';
  if (timingRiskBand === 'high') return 'high';
  if (timingRiskBand === 'medium') return 'medium';
  return 'low';
}

function getTimingIcon(timingRiskBand) {
  if (timingRiskBand === 'high') return <TrendingUp size={16} />;
  if (timingRiskBand === 'medium') return <Minus size={16} />;
  return <TrendingDown size={16} />;
}

function formatFareValueBand(value) {
  switch (value) {
    case 'cheap':
      return 'Cheap for this route';
    case 'good':
      return 'Below typical range';
    case 'fair':
      return 'Around typical range';
    case 'expensive':
      return 'Above typical range';
    case 'very_expensive':
      return 'Well above typical range';
    default:
      return 'No price context';
  }
}

function formatFareValueHelp(value) {
  switch (value) {
    case 'cheap':
    case 'good':
      return 'Lower than normal relative to recent route pricing.';
    case 'fair':
      return 'Close to the route’s normal pricing range.';
    case 'expensive':
    case 'very_expensive':
      return 'Higher than normal relative to recent route pricing.';
    default:
      return 'Route-relative price context is limited.';
  }
}

function formatTimingRiskBand(value) {
  switch (value) {
    case 'low':
      return 'No strong pressure';
    case 'medium':
      return 'Watch closely';
    case 'high':
      return 'Risk of further rise';
    default:
      return 'No clear timing call';
  }
}

function formatTimingRiskHelp(value, overrideActive) {
  if (overrideActive) {
    return 'Normal timing logic has been suppressed.';
  }

  switch (value) {
    case 'low':
      return 'Waiting does not currently look costly.';
    case 'medium':
      return 'There is some pressure in the current pattern.';
    case 'high':
      return 'Current signals suggest waiting could hurt.';
    default:
      return 'Timing evidence is limited.';
  }
}

function formatSignalQuality(value) {
  switch (value) {
    case 'strong':
      return 'Strong';
    case 'partial':
      return 'Partial';
    case 'weak':
      return 'Weak';
    default:
      return 'Partial';
  }
}

function formatFreshnessState(value) {
  switch (value) {
    case 'live':
      return 'Live';
    case 'recent':
      return 'Recent';
    case 'stale':
      return 'Stale';
    default:
      return 'Recent';
  }
}

function formatOverrideReason(value) {
  switch (value) {
    case 'anomaly_low_fare':
      return 'This fare is unusually low for the route and may reflect an anomaly rather than a stable market price.';
    case 'anomaly_high_fare':
      return 'This fare is unusually high for the route and should be treated with caution.';
    case 'weak_baseline':
      return 'There is not enough route pricing history to form a strong price-relative judgement.';
    case 'weak_momentum':
      return 'There is not enough recent route movement to form a strong timing judgement.';
    case 'stale_inputs':
      return 'The latest market inputs are not fresh enough for a strong booking signal.';
    default:
      return 'Signal quality is reduced, so the result should be treated cautiously.';
  }
}

function getRouteLabel(prediction) {
  if (prediction.route) return String(prediction.route);

  const origin =
    prediction.origin ??
    prediction.origin_iata ??
    prediction.from ??
    prediction.departure_origin;

  const destination =
    prediction.destination ??
    prediction.destination_iata ??
    prediction.dest ??
    prediction.to ??
    prediction.arrival_destination;

  if (origin && destination) {
    return `${String(origin).toUpperCase()} → ${String(destination).toUpperCase()}`;
  }

  return null;
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toNullableString(value) {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s ? s : null;
}

function buildScopedStyles(p) {
  return `
    .${p}-widget {
      --mizar-bg: linear-gradient(180deg, rgba(16, 23, 34, 0.97) 0%, rgba(11, 17, 27, 0.99) 100%);
      --mizar-border-subtle: rgba(255, 255, 255, 0.09);
      --mizar-text-primary: #F5F7FA;
      --mizar-text-secondary: rgba(245, 247, 250, 0.82);
      --mizar-text-muted: rgba(245, 247, 250, 0.58);
      --mizar-panel: rgba(255, 255, 255, 0.045);
      --mizar-panel-strong: rgba(255, 255, 255, 0.07);
      --mizar-shadow: 0 16px 36px rgba(0, 0, 0, 0.30);
      --mizar-risk: #7DD3A7;
      --mizar-risk-soft: rgba(125, 211, 167, 0.14);
      --mizar-glow: rgba(125, 211, 167, 0.18);

      position: relative;
      overflow: hidden;
      margin-top: 1rem;
      border: 1px solid var(--mizar-border-subtle);
      border-radius: 20px;
      background: var(--mizar-bg);
      box-shadow: var(--mizar-shadow);
      color: var(--mizar-text-primary);
      width: 100%;
      min-width: 0;
    }

    .${p}-widget.${p}-high {
      --mizar-risk: #F08989;
      --mizar-risk-soft: rgba(240, 137, 137, 0.14);
      --mizar-glow: rgba(240, 137, 137, 0.18);
    }

    .${p}-widget.${p}-medium {
      --mizar-risk: #F0B36A;
      --mizar-risk-soft: rgba(240, 179, 106, 0.14);
      --mizar-glow: rgba(240, 179, 106, 0.18);
    }

    .${p}-widget.${p}-low {
      --mizar-risk: #7DD3A7;
      --mizar-risk-soft: rgba(125, 211, 167, 0.14);
      --mizar-glow: rgba(125, 211, 167, 0.18);
    }

    .${p}-widget.${p}-caution {
      --mizar-risk: #F0B36A;
      --mizar-risk-soft: rgba(240, 179, 106, 0.16);
      --mizar-glow: rgba(240, 179, 106, 0.20);
    }

    .${p}-widget::before {
      content: "";
      position: absolute;
      inset: 0;
      background:
        radial-gradient(circle at top right, var(--mizar-glow) 0%, transparent 34%),
        radial-gradient(circle at bottom left, rgba(255,255,255,0.04) 0%, transparent 30%);
      pointer-events: none;
    }

    .${p}-inner {
      position: relative;
      z-index: 1;
      padding: 1rem;
    }

    .${p}-badgeRow {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-bottom: 0.8rem;
    }

    .${p}-routeBadge,
    .${p}-signalBadge,
    .${p}-metaChip {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.42rem 0.7rem;
      border-radius: 999px;
      font-size: 0.74rem;
      line-height: 1;
      font-weight: 700;
      letter-spacing: 0.04em;
      max-width: 100%;
    }

    .${p}-routeBadge,
    .${p}-signalBadge {
      background: rgba(255,255,255,0.05);
      color: var(--mizar-text-secondary);
      border: 1px solid rgba(255,255,255,0.04);
      text-transform: uppercase;
      white-space: nowrap;
    }

    .${p}-headline {
      margin: 0;
      font-size: 1.12rem;
      line-height: 1.2;
      font-weight: 800;
      letter-spacing: -0.025em;
    }

    .${p}-summary {
      margin-top: 0.55rem;
      color: var(--mizar-text-secondary);
      font-size: 0.92rem;
      line-height: 1.45;
    }

    .${p}-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.85rem;
      margin-top: 1rem;
    }

    .${p}-panel {
      background: var(--mizar-panel);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 14px;
      padding: 0.9rem;
      min-width: 0;
    }

    .${p}-panelStrong {
      background: var(--mizar-panel-strong);
    }

    .${p}-panelTitle {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      margin: 0 0 0.7rem;
      font-size: 0.8rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--mizar-text-muted);
    }

    .${p}-panelTitle svg {
      width: 16px;
      height: 16px;
      color: var(--mizar-risk);
      flex-shrink: 0;
    }

    .${p}-panelValue {
      font-size: 1rem;
      line-height: 1.2;
      font-weight: 800;
      color: var(--mizar-text-primary);
    }

    .${p}-panelHelp {
      margin-top: 0.3rem;
      color: var(--mizar-text-secondary);
      font-size: 0.84rem;
      line-height: 1.4;
    }

    .${p}-metaRow {
      display: flex;
      flex-wrap: wrap;
      gap: 0.55rem;
      margin-top: 0.95rem;
    }

    .${p}-metaChip {
      background: rgba(255,255,255,0.05);
      color: var(--mizar-text-secondary);
      border: 1px solid rgba(255,255,255,0.04);
      font-size: 0.76rem;
      text-transform: none;
      letter-spacing: 0;
      white-space: normal;
    }

    .${p}-metaChip strong {
      color: var(--mizar-text-primary);
      font-weight: 800;
    }

    .${p}-overrideBox {
      display: flex;
      align-items: flex-start;
      gap: 0.55rem;
      margin-top: 0.95rem;
      padding: 0.75rem 0.85rem;
      border-radius: 12px;
      background: var(--mizar-risk-soft);
      color: var(--mizar-risk);
      font-size: 0.84rem;
      line-height: 1.45;
      font-weight: 700;
    }

    .${p}-overrideBox svg {
      flex-shrink: 0;
      margin-top: 0.05rem;
    }

    .${p}-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      margin-top: 0.85rem;
      padding-top: 0.85rem;
      border-top: 1px solid rgba(255,255,255,0.08);
    }

    .${p}-footerCopy {
      font-size: 0.78rem;
      color: var(--mizar-text-muted);
      line-height: 1.4;
    }

    .${p}-footerCopy strong {
      color: var(--mizar-text-secondary);
      font-weight: 700;
    }

    .${p}-debug {
      margin-top: 0.7rem;
      padding-top: 0.7rem;
      border-top: 1px dashed rgba(255,255,255,0.08);
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.72rem;
      color: var(--mizar-text-muted);
      word-break: break-all;
    }

    .${p}-widget.${p}-compact .${p}-inner {
      padding: 0.9rem;
    }

    .${p}-widget.${p}-compact .${p}-headline {
      font-size: 1rem;
    }

    .${p}-widget.${p}-compact .${p}-summary {
      font-size: 0.84rem;
    }

    .${p}-widget.${p}-compact .${p}-panel {
      padding: 0.8rem;
    }

    @media (max-width: 820px) {
      .${p}-grid {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 640px) {
      .${p}-inner {
        padding: 0.9rem;
      }

      .${p}-headline {
        font-size: 1rem;
      }

      .${p}-summary {
        font-size: 0.86rem;
      }

      .${p}-panel {
        padding: 0.8rem;
      }

      .${p}-panelValue {
        font-size: 0.92rem;
      }

      .${p}-panelHelp,
      .${p}-overrideBox {
        font-size: 0.8rem;
      }

      .${p}-footer {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  `;
}